/* global axios */
import ApiClient from './ApiClient';

class KanbanApi extends ApiClient {
  constructor() {
    super('kanban/pipelines', { accountScoped: true });
  }

  get base() {
    return `${this.baseUrl()}/kanban`;
  }

  getPipelines() {
    return axios.get(`${this.base}/pipelines`);
  }

  createPipeline(pipeline) {
    return axios.post(`${this.base}/pipelines`, { pipeline });
  }

  updatePipeline(id, pipeline) {
    return axios.patch(`${this.base}/pipelines/${id}`, { pipeline });
  }

  deletePipeline(id) {
    return axios.delete(`${this.base}/pipelines/${id}`);
  }

  // filters: { inbox_ids: [], status: [], team_id, assignee_id, priority, q, per_stage }
  getBoard(pipelineId, filters = {}) {
    return axios.get(`${this.base}/pipelines/${pipelineId}/board`, {
      params: filters,
    });
  }

  // CRM-01h fase 3: "Carregar mais" — a próxima página de uma coluna, depois do cursor
  // `(position, id)` do último card que a tela tem, com os mesmos filtros do quadro.
  getStageCards(pipelineId, stageId, { cursor, filters = {} }) {
    return axios.get(
      `${this.base}/pipelines/${pipelineId}/stages/${stageId}/cards`,
      {
        params: {
          ...filters,
          after_position: cursor?.position,
          after_id: cursor?.id,
        },
      }
    );
  }

  // CRM-01h fase 3: "mover em bloco". A tela manda os cards marcados e as etapas inteiras; o
  // servidor resolve "todos desta etapa" pelos filtros, inclusive os cards não carregados.
  createBulkMove(payload) {
    return axios.post(`${this.base}/bulk_moves`, payload);
  }

  getBulkMove(id) {
    return axios.get(`${this.base}/bulk_moves/${id}`);
  }

  // CRM-01g: the card is built here instead of being picked up from a conversation
  // that already existed. Flat payload, not wrapped: contact, inbox and assignee are
  // siblings of the card's own fields, not attributes of it.
  createCard(payload) {
    return axios.post(`${this.base}/cards`, payload);
  }

  deleteCard(id) {
    return axios.delete(`${this.base}/cards/${id}`);
  }

  // Asked before creating, so the dialog can warn about a repeated phone number
  // instead of letting the operator find out afterwards.
  lookupContact(phoneNumber, pipelineId) {
    return axios.get(`${this.base}/cards/lookup`, {
      params: { phone_number: phoneNumber, pipeline_id: pipelineId },
    });
  }

  createStage(pipelineId, stage) {
    return axios.post(`${this.base}/pipelines/${pipelineId}/stages`, { stage });
  }

  updateStage(id, stage) {
    return axios.patch(`${this.base}/stages/${id}`, { stage });
  }

  deleteStage(id, moveToStageId) {
    return axios.delete(`${this.base}/stages/${id}`, {
      params: { move_to_stage_id: moveToStageId },
    });
  }

  // pipelineId travels in the body: /stages/reorder is a collection route, so the server
  // has no other way to know which board is being reordered.
  reorderStages(pipelineId, stages) {
    return axios.post(`${this.base}/stages/reorder`, {
      pipeline_id: pipelineId,
      stages,
    });
  }

  getFieldLayout() {
    return axios.get(`${this.base}/field_layout`);
  }

  saveFieldLayout(keys) {
    return axios.patch(`${this.base}/field_layout`, { keys });
  }

  // beforeId/afterId are the card's new neighbours. Sending neighbours instead of an
  // index lets two agents drop cards into the same column without fighting over a slot.
  moveCard(cardId, { stageId, beforeId, afterId }) {
    return axios.post(`${this.base}/cards/${cardId}/move`, {
      stage_id: stageId,
      before_id: beforeId,
      after_id: afterId,
    });
  }

  updateCard(cardId, card) {
    return axios.patch(`${this.base}/cards/${cardId}`, { card });
  }

  getCardDetails(cardId) {
    return axios.get(`${this.base}/cards/${cardId}/details`);
  }

  // CRM-04 R2-T7 (K2): a linha do tempo do negócio, da mais nova para a mais velha. `before` é o
  // `next_cursor` da página anterior; sem ele, vem a primeira página.
  getCardTimeline(cardId, { before, limit = 30 } = {}) {
    return axios.get(`${this.base}/cards/${cardId}/timeline`, {
      params: { before, limit },
    });
  }

  // CRM-04 R2-T7 (N12): os contatos do negócio. O servidor recusa o contato que já tem negócio aberto
  // no funil (409), o de outra conta (404) e tirar o último (422).
  addCardContact(cardId, contactId) {
    return axios.post(`${this.base}/cards/${cardId}/contacts`, {
      contact_id: contactId,
    });
  }

  removeCardContact(cardId, contactId) {
    return axios.delete(`${this.base}/cards/${cardId}/contacts/${contactId}`);
  }

  // Os campos de um contato do negócio. Era `PATCH cards/:id/contact`, que só via o contato da conversa
  // do card.
  updateCardContact(cardId, contactId, contact) {
    return axios.patch(`${this.base}/cards/${cardId}/contacts/${contactId}`, {
      contact,
    });
  }

  // CRM-04 R2-T7 (N15): juntar, só o administrador e só no mesmo funil. O card da rota fica; o
  // `absorvido_id` sai do quadro.
  mergeCards(cardId, absorvidoId) {
    return axios.post(`${this.base}/cards/${cardId}/merge`, {
      absorvido_id: absorvidoId,
    });
  }

  generateSummary(cardId) {
    return axios.post(`${this.base}/cards/${cardId}/summary`);
  }
}

export default new KanbanApi();
